import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { 
  Calendar, MapPin, Clock, Users, DollarSign, ChevronLeft,
  Share2, Ticket, PartyPopper, ExternalLink, CheckCircle, XCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    fetchEvent();
    
    // Check for payment return
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}`);
      setEvent(response.data);
    } catch (error) {
      console.error('Failed to fetch event:', error);
      toast.error('Event not found');
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 10) {
      setCheckingPayment(false);
      toast.error('Payment verification timed out. Please check your email for confirmation.');
      return;
    }

    setCheckingPayment(true);
    try {
      const response = await axios.get(`${API}/events/${eventId}/payment-status?session_id=${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! You\'re registered for the event.');
        setCheckingPayment(false);
        fetchEvent();
        // Clear URL params
        window.history.replaceState({}, '', `/events/${eventId}`);
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingPayment(false);
        window.history.replaceState({}, '', `/events/${eventId}`);
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  const handleRegister = async () => {
    console.log('Register button clicked');
    setRegistering(true);
    try {
      console.log('Sending registration request...');
      const response = await axios.post(`${API}/events/${eventId}/register`, {
        origin_url: window.location.origin
      });
      
      console.log('Registration response:', response.data);
      
      if (response.data.type === 'payment_required') {
        // Redirect to Stripe checkout
        console.log('Redirecting to Stripe:', response.data.checkout_url);
        window.location.href = response.data.checkout_url;
      } else {
        toast.success(response.data.message);
        fetchEvent();
      }
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(errorMessage);
    } finally {
      setRegistering(false);
    }
  };

  const handleCancelRegistration = async () => {
    try {
      await axios.delete(`${API}/events/${eventId}/register`);
      toast.success('Registration cancelled');
      fetchEvent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel registration');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      await navigator.share({
        title: event.title,
        text: `Check out this event: ${event.title}`,
        url: window.location.href
      });
    } else {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (loading || checkingPayment) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#A1A1AA]">{checkingPayment ? 'Verifying payment...' : 'Loading event...'}</p>
        </div>
      </div>
    );
  }

  if (!event) return null;

  // Safely get registration state with explicit defaults
  const isRegistered = event.is_registered === true;
  const isFull = event.is_full === true;
  const hasWaitlist = event.has_waitlist === true;
  const canRegister = !isRegistered && !isFull;
  const isOnWaitlist = event.user_registration?.status === 'waitlist';
  
  // Debug logging for troubleshooting
  console.log('Event state:', {
    title: event.title,
    isRegistered,
    isFull,
    hasWaitlist,
    canRegister,
    is_paid: event.is_paid,
    price: event.price,
    user_registration: event.user_registration
  });

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Hero Image */}
      <div className="relative h-64 md:h-80">
        {event.image_data ? (
          <img 
            src={event.image_data.startsWith('data:') ? event.image_data : `data:image/jpeg;base64,${event.image_data}`}
            alt={event.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#FF2E63] to-[#7000FF] flex items-center justify-center">
            <PartyPopper className="w-24 h-24 text-white/50" />
          </div>
        )}
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
        
        {/* Back button */}
        <button 
          onClick={() => navigate('/events')}
          className="absolute top-4 left-4 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        
        {/* Share button */}
        <button 
          onClick={handleShare}
          className="absolute top-4 right-4 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
        >
          <Share2 className="w-5 h-5" />
        </button>
        
        {/* Category badge */}
        <span className="absolute bottom-4 left-4 px-3 py-1 bg-black/50 backdrop-blur-sm rounded-full text-sm font-medium text-white capitalize">
          {event.category}
        </span>
      </div>

      {/* Content */}
      <div className="px-4 pb-32 -mt-4 relative z-10">
        {/* Title & Price */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <h1 className="text-2xl font-bold text-white">{event.title}</h1>
          <span className={`px-3 py-1.5 rounded-lg text-lg font-bold flex-shrink-0 ${
            event.is_paid ? 'bg-[#FF2E63] text-white' : 'bg-[#22c55e] text-white'
          }`}>
            {event.is_paid ? `$${event.price}` : 'FREE'}
          </span>
        </div>

        {/* Event Details */}
        <div className="space-y-4 mb-6">
          <div className="flex items-start gap-4 p-4 bg-[#111] rounded-xl border border-white/10">
            <div className="w-10 h-10 bg-[#FF2E63]/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Calendar className="w-5 h-5 text-[#FF2E63]" />
            </div>
            <div>
              <p className="text-white font-medium">{formatDate(event.event_date)}</p>
              <p className="text-[#A1A1AA] text-sm">
                {formatTime(event.start_time)}
                {event.end_time && ` - ${formatTime(event.end_time)}`}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-[#111] rounded-xl border border-white/10">
            <div className="w-10 h-10 bg-[#08D9D6]/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <MapPin className="w-5 h-5 text-[#08D9D6]" />
            </div>
            <div className="flex-1">
              <p className="text-white font-medium">{event.location_name}</p>
              <p className="text-[#A1A1AA] text-sm">{event.location_address}</p>
            </div>
            {event.location_lat && event.location_lng && (
              <a 
                href={`https://maps.google.com/?q=${event.location_lat},${event.location_lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#08D9D6] hover:underline"
              >
                <ExternalLink className="w-5 h-5" />
              </a>
            )}
          </div>

          {event.capacity && (
            <div className="flex items-start gap-4 p-4 bg-[#111] rounded-xl border border-white/10">
              <div className="w-10 h-10 bg-[#7000FF]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-[#7000FF]" />
              </div>
              <div>
                <p className="text-white font-medium">
                  {event.registered_count || 0} / {event.capacity} attending
                </p>
                <p className="text-[#A1A1AA] text-sm">
                  {event.spots_left > 0 ? `${event.spots_left} spots left` : 
                   event.has_waitlist ? `Waitlist available (${event.waitlist_count || 0} waiting)` : 'Event is full'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Description */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-3">About this event</h2>
          <p className="text-[#A1A1AA] leading-relaxed whitespace-pre-wrap">{event.description}</p>
        </div>

        {/* Registration Status */}
        {isRegistered && (
          <div className="mb-6 p-4 bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-xl flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-[#22c55e]" />
            <div>
              <p className="text-[#22c55e] font-medium">You're registered!</p>
              <p className="text-[#A1A1AA] text-sm">
                {event.user_registration?.status === 'paid' ? 'Payment confirmed' : 'Registration confirmed'}
              </p>
            </div>
          </div>
        )}

        {isOnWaitlist && (
          <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl flex items-center gap-3">
            <Clock className="w-6 h-6 text-yellow-500" />
            <div>
              <p className="text-yellow-500 font-medium">You're on the waitlist</p>
              <p className="text-[#A1A1AA] text-sm">We'll notify you if a spot opens up</p>
            </div>
          </div>
        )}
      </div>

      {/* Fixed Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-[#050505]/95 backdrop-blur border-t border-white/10">
        {isRegistered ? (
          <div className="flex gap-3">
            <Button 
              onClick={() => navigate('/events')}
              className="flex-1 h-12 bg-white/10 hover:bg-white/20 text-white"
            >
              Back to Events
            </Button>
            {event.user_registration?.status !== 'paid' && (
              <Button 
                onClick={handleCancelRegistration}
                variant="outline"
                className="h-12 border-red-500 text-red-500 hover:bg-red-500/10"
              >
                Cancel
              </Button>
            )}
          </div>
        ) : (
          <Button 
            data-testid="register-btn"
            onClick={() => {
              console.log('Register button clicked!');
              if (registering) {
                console.log('Already registering, ignoring click');
                return;
              }
              if (isRegistered) {
                toast.info('You are already registered for this event');
                return;
              }
              if (isFull && !hasWaitlist) {
                toast.error('This event is full');
                return;
              }
              handleRegister();
            }}
            disabled={registering}
            className="w-full h-12 bg-[#FF2E63] hover:bg-[#E01E50] text-white font-semibold disabled:opacity-50 cursor-pointer"
          >
            {registering ? (
              <span className="flex items-center gap-2">
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Processing...
              </span>
            ) : isFull && hasWaitlist ? (
              <span className="flex items-center gap-2">
                <Ticket className="w-5 h-5" />
                Join Waitlist
              </span>
            ) : isFull ? (
              'Event Full'
            ) : (
              <span className="flex items-center gap-2">
                <Ticket className="w-5 h-5" />
                {event.is_paid ? `Register • $${event.price}` : 'Register for Free'}
              </span>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default EventDetail;
